import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Clock, Smartphone, Sparkles, ArrowRight, PlayCircle, MessageSquare, Image as ImageIcon, ChevronDown, X } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-1">
    {children}
  </motion.div>;
const StaggerContainer = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <motion.div initial="hidden" whileInView="visible" viewport={{
  once: true,
  margin: '-50px'
}} variants={{
  visible: {
    transition: {
      staggerChildren: 0.15
    }
  }
}} className={className} data-id="element-2">
    {children}
  </motion.div>;
const StaggerItem = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <motion.div variants={{
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
}} className={className} data-id="element-3">
    {children}
  </motion.div>;
export function LandingPage() {
  const imageUrl = 'https://cdn.magicpatterns.com/uploads/7vaUNhVhLfnCBbQk3XdVGB/IA_Expres_3_mini_negocios.png';
  const [legalModal, setLegalModal] = useState<'privacy' | 'terms' | null>(null);
  const scrollToOffer = () => {
    document.getElementById('offer-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-cream overflow-hidden" data-id="element-4">
      {/* HERO SECTION */}
      <section className="relative bg-navy text-white pt-20 pb-24 lg:pt-32 lg:pb-32 overflow-hidden" data-id="element-5">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-gold via-navy to-navy" data-id="element-6"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10" data-id="element-7">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-8">
            <FadeIn data-id="element-9">
              <div className="inline-flex items-center space-x-2 bg-navy-light px-4 py-2 rounded-full mb-8 border border-gold/30" data-id="element-10">
                <Sparkles className="w-4 h-4 text-gold" data-id="element-11" />
                <span className="text-sm font-medium text-gold-light tracking-wide uppercase" data-id="element-12">
                  El Método IA Exprés
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-white" data-id="element-13">
                Deja de "Punch-in" el Reloj y Lanza Tu Primer{' '}
                <span className="text-gold italic" data-id="element-14">Mini-Negocio Visual</span>{' '}
                con IA en 48 Horas
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mb-10 font-light leading-relaxed max-w-xl" data-id="element-15">
                La guía paso a paso para mujeres que quieren liberarse del
                trabajo rutinario y empezar a generar ingresos desde su celular
                con Inteligencia Artificial.
              </p>
              <button onClick={scrollToOffer} className="w-full sm:w-auto bg-gold hover:bg-gold-light text-navy font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-300 shadow-gold-glow flex items-center justify-center space-x-2 group" data-id="element-16">
                <span data-id="element-17">Emprende con IA Ahora</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-18" />
              </button>
              <p className="mt-4 text-sm text-gray-400 font-light" data-id="element-19">
                Toma el control de tu tiempo y de tu futuro, empieza hoy.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="relative" data-id="element-20">
              <div className="absolute inset-0 bg-gold rounded-2xl transform rotate-3 scale-105 opacity-20 blur-lg" data-id="element-21"></div>
              <img src={imageUrl} alt="Mujer usando su celular y laptop para crear un mini-negocio con IA" className="relative rounded-2xl shadow-2xl border border-white/10 object-cover w-full h-auto" data-id="element-22" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* HOOK & PROBLEM SECTION */}
      <section className="py-24 bg-cream" data-id="element-23">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center" data-id="element-24">
          <FadeIn data-id="element-25">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-8" data-id="element-26">
              ¿Harta de sentir que el reloj controla tu vida?
            </h2>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-27">
              Cada día, entras, sales, ponchas, repites. Pero ahora tienes en tu
              mano algo mucho más poderoso:{' '}
              <strong className="text-navy font-semibold" data-id="element-28">tu celular</strong>.
            </p>
            <p className="text-lg text-gray-700 mb-12 leading-relaxed" data-id="element-29">
              Lo que antes era solo una herramienta para redes, hoy puede
              convertirse en tu nueva fuente de ingresos.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-cream-dark text-left" data-id="element-30">
            <h3 className="text-2xl font-bold text-navy mb-6 font-playfair" data-id="element-31">
              El Problema:
            </h3>
            <p className="text-gray-700 leading-relaxed mb-0" data-id="element-32">
              La rutina del trabajo tradicional te agobia. Levantarte temprano,
              el tráfico, las largas horas, y al final del mes, el cheque que
              nunca alcanza. Has pensado muchas veces en emprender, pero no
              sabías por dónde empezar.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* WHY PAST ATTEMPTS FAILED */}
      <section className="py-24 bg-white" data-id="element-33">
        <div className="max-w-5xl mx-auto px-6 lg:px-8" data-id="element-34">
          <FadeIn className="text-center mb-16" data-id="element-35">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4" data-id="element-36">
              Por qué lo que intentaste antes no funcionó
            </h2>
            <div className="w-24 h-1 bg-gold mx-auto" data-id="element-37"></div>
          </FadeIn>

          <StaggerContainer className="grid md:grid-cols-3 gap-8" data-id="element-38">
            <StaggerItem className="bg-cream p-8 rounded-xl border border-cream-dark" data-id="element-39">
              <XCircle className="w-10 h-10 text-red-400 mb-6" data-id="element-40" />
              <p className="text-gray-800 font-medium leading-relaxed" data-id="element-41">
                Intentaste vender productos físicos, pero terminaste con más
                gastos que ingresos.
              </p>
            </StaggerItem>
            <StaggerItem className="bg-cream p-8 rounded-xl border border-cream-dark" data-id="element-42">
              <XCircle className="w-10 h-10 text-red-400 mb-6" data-id="element-43" />
              <p className="text-gray-800 font-medium leading-relaxed" data-id="element-44">
                Probaste cursos complicados llenos de teoría, pero nunca
                llegaron a lo práctico.
              </p>
            </StaggerItem>
            <StaggerItem className="bg-cream p-8 rounded-xl border border-cream-dark" data-id="element-45">
              <XCircle className="w-10 h-10 text-red-400 mb-6" data-id="element-46" />
              <p className="text-gray-800 font-medium leading-relaxed" data-id="element-47">
                Pensaste que necesitabas una computadora costosa o conocimientos
                técnicos avanzados.
              </p>
            </StaggerItem>
          </StaggerContainer>
        </div>
      </section>

      {/* THE SOLUTION (MECANISMO ÚNICO) */}
      <section className="py-24 bg-navy text-white relative overflow-hidden" data-id="element-48">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-49">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold rounded-full blur-3xl" data-id="element-50"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-gold rounded-full blur-3xl" data-id="element-51"></div>
        </div>

        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10" data-id="element-52">
          <FadeIn className="text-center mb-16" data-id="element-53">
            <h2 className="text-3xl md:text-4xl font-bold text-gold mb-6" data-id="element-54">
              Te presentamos el método IA Exprés
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-55">
              Es un sistema práctico que combina tres herramientas de
              Inteligencia Artificial que ya funcionan en tu celular. Es simple,
              rápido y 100% móvil.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-56">
            <FadeIn delay={0.1} className="bg-navy-light p-8 rounded-2xl border border-white/10 hover:border-gold/50 transition-colors" data-id="element-57">
              <div className="w-14 h-14 bg-navy rounded-xl flex items-center justify-center mb-6 border border-white/5" data-id="element-58">
                <ImageIcon className="w-7 h-7 text-gold" data-id="element-59" />
              </div>
              <h3 className="text-xl font-bold mb-4 font-playfair" data-id="element-60">
                Nano Banana
              </h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-61">
                Crea paquetes de imágenes personalizadas y profesionales para
                negocios locales en minutos.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-navy-light p-8 rounded-2xl border border-white/10 hover:border-gold/50 transition-colors" data-id="element-62">
              <div className="w-14 h-14 bg-navy rounded-xl flex items-center justify-center mb-6 border border-white/5" data-id="element-63">
                <PlayCircle className="w-7 h-7 text-gold" data-id="element-64" />
              </div>
              <h3 className="text-xl font-bold mb-4 font-playfair" data-id="element-65">Sora</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-66">
                Genera videos cortos impactantes que capturan la esencia de
                cualquier marca o menú.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-navy-light p-8 rounded-2xl border border-white/10 hover:border-gold/50 transition-colors" data-id="element-67">
              <div className="w-14 h-14 bg-navy rounded-xl flex items-center justify-center mb-6 border border-white/5" data-id="element-68">
                <MessageSquare className="w-7 h-7 text-gold" data-id="element-69" />
              </div>
              <h3 className="text-xl font-bold mb-4 font-playfair" data-id="element-70">
                WhatsApp Business
              </h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-71">
                Automatiza respuestas con IA para que el negocio atienda a sus
                clientes 24/7 sin esfuerzo.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-24 bg-cream" data-id="element-72">
        <div className="max-w-4xl mx-auto px-6 lg:px-8" data-id="element-73">
          <FadeIn className="text-center mb-16" data-id="element-74">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4" data-id="element-75">
              ¿Qué incluye la guía?
            </h2>
            <div className="w-24 h-1 bg-gold mx-auto" data-id="element-76"></div>
          </FadeIn>

          <div className="bg-white rounded-2xl shadow-sm border border-cream-dark p-8 md:p-12" data-id="element-77">
            <StaggerContainer className="space-y-6" data-id="element-78">
              <StaggerItem className="flex items-start" data-id="element-79">
                <CheckCircle2 className="w-6 h-6 text-gold mt-1 flex-shrink-0 mr-4" data-id="element-80" />
                <div data-id="element-81">
                  <h4 className="text-lg font-bold text-navy mb-1" data-id="element-82">
                    Guía principal
                  </h4>
                  <p className="text-gray-600" data-id="element-83">
                    "IA Exprés 2026: Mini-Negocios Visuales y Automatización con
                    IA".
                  </p>
                </div>
              </StaggerItem>
              <StaggerItem className="flex items-start" data-id="element-84">
                <CheckCircle2 className="w-6 h-6 text-gold mt-1 flex-shrink-0 mr-4" data-id="element-85" />
                <div data-id="element-86">
                  <h4 className="text-lg font-bold text-navy mb-1" data-id="element-87">
                    Paso a paso detallado
                  </h4>
                  <p className="text-gray-600" data-id="element-88">
                    Capítulo a capítulo: cómo crear imágenes con Nano Banana,
                    videos con Sora, y automatizar WhatsApp.
                  </p>
                </div>
              </StaggerItem>
              <StaggerItem className="flex items-start" data-id="element-89">
                <CheckCircle2 className="w-6 h-6 text-gold mt-1 flex-shrink-0 mr-4" data-id="element-90" />
                <div data-id="element-91">
                  <h4 className="text-lg font-bold text-navy mb-1" data-id="element-92">
                    Plantillas listas para usar
                  </h4>
                  <p className="text-gray-600" data-id="element-93">
                    Plantillas de prompts para copiar y pegar sin
                    complicaciones.
                  </p>
                </div>
              </StaggerItem>
              <StaggerItem className="flex items-start" data-id="element-94">
                <CheckCircle2 className="w-6 h-6 text-gold mt-1 flex-shrink-0 mr-4" data-id="element-95" />
                <div data-id="element-96">
                  <h4 className="text-lg font-bold text-navy mb-1" data-id="element-97">
                    Checklist de acción
                  </h4>
                  <p className="text-gray-600" data-id="element-98">
                    Checklist para ejecutar cada mini-negocio en menos de 48
                    horas.
                  </p>
                </div>
              </StaggerItem>
              <StaggerItem className="flex items-start bg-cream p-4 rounded-lg border border-gold/20 mt-8" data-id="element-99">
                <Sparkles className="w-6 h-6 text-gold mt-1 flex-shrink-0 mr-4" data-id="element-100" />
                <div data-id="element-101">
                  <h4 className="text-lg font-bold text-navy mb-1" data-id="element-102">
                    Bonus Especial
                  </h4>
                  <p className="text-gray-800 font-medium" data-id="element-103">
                    Guión exacto para contactar negocios locales y cerrar tu
                    primer cliente sin ser experta en ventas.
                  </p>
                </div>
              </StaggerItem>
            </StaggerContainer>
          </div>
        </div>
      </section>

      {/* WHO IS THIS FOR / NOT FOR */}
      <section className="py-24 bg-white" data-id="element-104">
        <div className="max-w-6xl mx-auto px-6 lg:px-8" data-id="element-105">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-106">
            <FadeIn data-id="element-107">
              <h3 className="text-2xl font-bold text-navy mb-8 font-playfair flex items-center" data-id="element-108">
                <span className="bg-green-100 text-green-700 p-2 rounded-lg mr-3" data-id="element-109">
                  <CheckCircle2 className="w-6 h-6" data-id="element-110" />
                </span>
                Esto SÍ es para ti si:
              </h3>
              <ul className="space-y-6" data-id="element-111">
                <li className="flex items-start" data-id="element-112">
                  <div className="w-2 h-2 bg-gold rounded-full mt-2 mr-4 flex-shrink-0" data-id="element-113"></div>
                  <p className="text-gray-700" data-id="element-114">
                    Mujeres que trabajan en empleos agotadores y necesitan un
                    ingreso extra.
                  </p>
                </li>
                <li className="flex items-start" data-id="element-115">
                  <div className="w-2 h-2 bg-gold rounded-full mt-2 mr-4 flex-shrink-0" data-id="element-116"></div>
                  <p className="text-gray-700" data-id="element-117">
                    Personas con poco tiempo, que quieren trabajar desde su
                    celular.
                  </p>
                </li>
                <li className="flex items-start" data-id="element-118">
                  <div className="w-2 h-2 bg-gold rounded-full mt-2 mr-4 flex-shrink-0" data-id="element-119"></div>
                  <p className="text-gray-700" data-id="element-120">
                    Emprendedoras que no quieren lidiar con productos físicos ni
                    inversiones grandes.
                  </p>
                </li>
                <li className="flex items-start" data-id="element-121">
                  <div className="w-2 h-2 bg-gold rounded-full mt-2 mr-4 flex-shrink-0" data-id="element-122"></div>
                  <p className="text-gray-700" data-id="element-123">
                    Mujeres que buscan resultados rápidos y prácticos.
                  </p>
                </li>
              </ul>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-124">
              <h3 className="text-2xl font-bold text-navy mb-8 font-playfair flex items-center" data-id="element-125">
                <span className="bg-red-100 text-red-700 p-2 rounded-lg mr-3" data-id="element-126">
                  <XCircle className="w-6 h-6" data-id="element-127" />
                </span>
                Esto NO es para ti si:
              </h3>
              <ul className="space-y-6" data-id="element-128">
                <li className="flex items-start" data-id="element-129">
                  <div className="w-2 h-2 bg-gray-300 rounded-full mt-2 mr-4 flex-shrink-0" data-id="element-130"></div>
                  <p className="text-gray-600" data-id="element-131">
                    Quienes buscan "dinero fácil" sin poner ningún esfuerzo.
                  </p>
                </li>
                <li className="flex items-start" data-id="element-132">
                  <div className="w-2 h-2 bg-gray-300 rounded-full mt-2 mr-4 flex-shrink-0" data-id="element-133"></div>
                  <p className="text-gray-600" data-id="element-134">
                    Personas que no están dispuestas a aprender nada nuevo.
                  </p>
                </li>
                <li className="flex items-start" data-id="element-135">
                  <div className="w-2 h-2 bg-gray-300 rounded-full mt-2 mr-4 flex-shrink-0" data-id="element-136"></div>
                  <p className="text-gray-600" data-id="element-137">
                    Quienes prefieren quejarse y quedarse en la rutina de su
                    empleo actual.
                  </p>
                </li>
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-cream" data-id="element-138">
        <div className="max-w-5xl mx-auto px-6 lg:px-8" data-id="element-139">
          <FadeIn className="text-center mb-16" data-id="element-140">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4" data-id="element-141">
              ¿Cómo funciona?
            </h2>
            <p className="text-lg text-gray-600" data-id="element-142">
              Tres pasos simples para lanzar tu negocio.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-143">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-gold/30 z-0" data-id="element-144"></div>

            <FadeIn delay={0.1} className="relative z-10 text-center" data-id="element-145">
              <div className="w-24 h-24 bg-navy text-gold rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg border-4 border-cream" data-id="element-146">
                1
              </div>
              <h3 className="text-xl font-bold text-navy mb-3 font-playfair" data-id="element-147">
                Descarga y Configura
              </h3>
              <p className="text-gray-600" data-id="element-148">
                Descarga la guía y configura las tres herramientas de IA
                directamente en tu celular.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="relative z-10 text-center" data-id="element-149">
              <div className="w-24 h-24 bg-navy text-gold rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg border-4 border-cream" data-id="element-150">
                2
              </div>
              <h3 className="text-xl font-bold text-navy mb-3 font-playfair" data-id="element-151">
                Sigue los Pasos
              </h3>
              <p className="text-gray-600" data-id="element-152">
                Copia y pega nuestros prompts para crear tus primeros paquetes
                de imágenes, videos y automatización.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="relative z-10 text-center" data-id="element-153">
              <div className="w-24 h-24 bg-gold text-navy rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-lg border-4 border-cream" data-id="element-154">
                3
              </div>
              <h3 className="text-xl font-bold text-navy mb-3 font-playfair" data-id="element-155">
                Ofrece y Cobra
              </h3>
              <p className="text-gray-600" data-id="element-156">
                Ofrece tus servicios a negocios locales o en redes usando
                nuestro guión y cobra tus primeros ingresos.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* PROOF / BELIEVABILITY */}
      <section className="py-24 bg-navy text-white relative" data-id="element-157">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center" data-id="element-158">
          <FadeIn data-id="element-159">
            <Sparkles className="w-12 h-12 text-gold mx-auto mb-8 opacity-80" data-id="element-160" />
            <h2 className="text-3xl md:text-4xl font-bold text-gold mb-8 font-playfair italic leading-tight" data-id="element-161">
              "Imagina esto: en solo 7 días..."
            </h2>
            <p className="text-xl text-gray-300 mb-6 leading-relaxed font-light" data-id="element-162">
              Un pequeño restaurante local que atendiste ahora tiene un perfil
              de Instagram con imágenes profesionales, un video corto que
              captura la esencia de su menú y un asistente automatizado
              respondiendo a sus clientes.
            </p>
            <p className="text-xl text-white font-medium leading-relaxed" data-id="element-163">
              Ese negocio ahora gana más clientes, y tú has cobrado tu primer
              pago. <br className="hidden md:block" data-id="element-164" />
              <span className="text-gold" data-id="element-165">Esto es real y posible.</span>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-166">
        <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-167">
          <FadeIn className="text-center mb-16" data-id="element-168">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4" data-id="element-169">
              Preguntas Frecuentes
            </h2>
            <div className="w-24 h-1 bg-gold mx-auto" data-id="element-170"></div>
          </FadeIn>

          <StaggerContainer className="space-y-6" data-id="element-171">
            {[{
            q: '¿Necesito experiencia en diseño o tecnología?',
            a: 'No. Las herramientas que te damos funcionan con prompts simples y te guiamos paso a paso.'
          }, {
            q: '¿Cuánto puedo cobrar por mis servicios?',
            a: 'Sugerimos entre $75 y $150 por paquetes visuales completos, o incluso más si agregas personalización.'
          }, {
            q: '¿Cuánto tiempo me tomará?',
            a: 'Con práctica, puedes crear un paquete completo de imágenes y video en menos de 2 horas.'
          }, {
            q: '¿Puedo hacerlo mientras sigo en mi trabajo actual?',
            a: 'Sí. Este método está diseñado para personas con poco tiempo. Puedes comenzar con tus primeras ventas sin renunciar a tu empleo.'
          }, {
            q: '¿Qué pasa si no soy buena vendiendo?',
            a: 'Te damos un script simple para contactar a negocios y cerrar tus primeros clientes sin sonar a vendedora tradicional.'
          }].map((faq, index) => <StaggerItem key={index} className="bg-cream p-6 rounded-xl border border-cream-dark" data-id="element-172">
                <h4 className="text-lg font-bold text-navy mb-3 flex items-start" data-id="element-173">
                  <span className="text-gold mr-3" data-id="element-174">Q.</span>
                  {faq.q}
                </h4>
                <p className="text-gray-700 pl-8 leading-relaxed" data-id="element-175">{faq.a}</p>
              </StaggerItem>)}
          </StaggerContainer>
        </div>
      </section>

      {/* OFFER & PRICE BLOCK */}
      <section id="offer-section" className="py-24 bg-cream relative" data-id="element-176">
        <div className="max-w-5xl mx-auto px-6 lg:px-8" data-id="element-177">
          <FadeIn className="bg-navy rounded-3xl shadow-2xl overflow-hidden border border-gold/20" data-id="element-178">
            <div className="grid md:grid-cols-2" data-id="element-179">
              <div className="p-10 md:p-14 flex flex-col justify-center" data-id="element-180">
                <div className="inline-block bg-gold/20 text-gold px-4 py-1.5 rounded-full text-sm font-semibold mb-6 w-fit" data-id="element-181">
                  Oferta Especial
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-playfair leading-tight" data-id="element-182">
                  Empieza tu Mini-Negocio Hoy Mismo
                </h2>
                <p className="text-gray-300 mb-8 text-lg" data-id="element-183">
                  Hoy puedes llevarte la guía completa, con todos los capítulos,
                  plantillas y bonos, por un precio único.
                </p>

                <div className="mb-8" data-id="element-184">
                  <div className="flex items-center gap-3 mb-1" data-id="element-185">
                    <span className="text-2xl text-gray-500 line-through font-medium" data-id="element-186">
                      $27
                    </span>
                    <span className="bg-red-500/20 text-red-400 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide" data-id="element-187">
                      -76% Desc.
                    </span>
                  </div>
                  <div className="flex items-baseline" data-id="element-188">
                    <span className="text-6xl font-bold text-gold" data-id="element-189">$6.39</span>
                    <span className="text-gray-400 ml-3 text-lg" data-id="element-190">
                      Un solo pago.
                      <br data-id="element-191" />
                      Acceso de por vida.
                    </span>
                  </div>
                </div>

                <a href="https://iaexpres2026.com/products/ia-expres-2026-3-mini-negocios-desde-el-celular" target="_blank" rel="noopener noreferrer" className="w-full bg-gold hover:bg-gold-light text-navy font-bold text-xl px-8 py-5 rounded-xl transition-all duration-300 shadow-gold-glow flex items-center justify-center space-x-3 group no-underline" data-id="element-192">
                  <span data-id="element-193">Emprende con IA Ahora</span>
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-194" />
                </a>
                <p className="text-center text-gray-400 text-sm mt-4 font-light" data-id="element-195">
                  Toma el control de tu tiempo y de tu futuro, empieza hoy.
                </p>
              </div>

              <div className="relative h-64 md:h-auto bg-navy-light" data-id="element-196">
                <img src={imageUrl} alt="IA Exprés Guía" className="absolute inset-0 w-full h-full object-cover opacity-90 mix-blend-luminosity hover:mix-blend-normal transition-all duration-700" data-id="element-197" />
                <div className="absolute inset-0 bg-gradient-to-r from-navy to-transparent" data-id="element-198"></div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-navy-dark py-10 text-center text-gray-500 text-sm" data-id="element-199">
        <div className="max-w-4xl mx-auto px-6" data-id="element-200">
          <p className="mb-4" data-id="element-201">
            © 2026 IA Exprés. Todos los derechos reservados.
          </p>
          <div className="flex items-center justify-center gap-6" data-id="element-202">
            <button onClick={() => setLegalModal('privacy')} className="text-gray-400 hover:text-gold transition-colors underline underline-offset-2" data-id="element-203">
              Política de Privacidad
            </button>
            <span className="text-gray-600" data-id="element-204">|</span>
            <button onClick={() => setLegalModal('terms')} className="text-gray-400 hover:text-gold transition-colors underline underline-offset-2" data-id="element-205">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* LEGAL MODAL */}
      <AnimatePresence data-id="element-206">
        {legalModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setLegalModal(null)} data-id="element-207">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-208">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-209">
                <h3 className="text-xl font-bold text-navy font-playfair" data-id="element-210">
                  {legalModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setLegalModal(null)} className="text-gray-400 hover:text-navy transition-colors p-1 rounded-lg hover:bg-gray-100" data-id="element-211">
                  <X className="w-5 h-5" data-id="element-212" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-left text-gray-700 leading-relaxed space-y-4" data-id="element-213">
                {legalModal === 'privacy' ? <>
                    <p className="text-sm text-gray-500" data-id="element-214">
                      Última actualización: marzo 2026
                    </p>
                    <h4 className="font-bold text-navy text-lg" data-id="element-215">
                      1. Información que Recopilamos
                    </h4>
                    <p data-id="element-216">
                      Al adquirir nuestros productos digitales, recopilamos la
                      información necesaria para procesar tu compra, incluyendo
                      tu nombre, dirección de correo electrónico y datos de
                      pago. No almacenamos información de tarjetas de crédito
                      directamente; los pagos son procesados por plataformas de
                      pago seguras de terceros.
                    </p>
                    <h4 className="font-bold text-navy text-lg" data-id="element-217">
                      2. Uso de la Información
                    </h4>
                    <p data-id="element-218">
                      Utilizamos tu información personal exclusivamente para:
                      entregar el producto digital adquirido, enviarte
                      actualizaciones relacionadas con tu compra, y mejorar
                      nuestros servicios. No vendemos, alquilamos ni compartimos
                      tu información personal con terceros con fines de
                      marketing.
                    </p>
                    <h4 className="font-bold text-navy text-lg" data-id="element-219">
                      3. Protección de Datos
                    </h4>
                    <p data-id="element-220">
                      Implementamos medidas de seguridad técnicas y
                      organizativas para proteger tu información personal contra
                      acceso no autorizado, alteración, divulgación o
                      destrucción.
                    </p>
                    <h4 className="font-bold text-navy text-lg" data-id="element-221">4. Cookies</h4>
                    <p data-id="element-222">
                      Nuestro sitio web puede utilizar cookies para mejorar tu
                      experiencia de navegación y analizar el tráfico del sitio.
                      Puedes configurar tu navegador para rechazar cookies,
                      aunque esto podría afectar algunas funcionalidades.
                    </p>
                    <h4 className="font-bold text-navy text-lg" data-id="element-223">
                      5. Tus Derechos
                    </h4>
                    <p data-id="element-224">
                      Tienes derecho a acceder, rectificar o eliminar tu
                      información personal en cualquier momento. Para ejercer
                      estos derechos, puedes contactarnos a través de los
                      canales indicados en nuestro sitio.
                    </p>
                    <h4 className="font-bold text-navy text-lg" data-id="element-225">
                      6. Cambios a esta Política
                    </h4>
                    <p data-id="element-226">
                      Nos reservamos el derecho de actualizar esta política de
                      privacidad en cualquier momento. Los cambios serán
                      publicados en esta página con la fecha de actualización
                      correspondiente.
                    </p>
                  </> : <>
                    <p className="text-sm text-gray-500" data-id="element-227">
                      Última actualización: marzo 2026
                    </p>
                    <h4 className="font-bold text-navy text-lg" data-id="element-228">
                      1. Aceptación de los Términos
                    </h4>
                    <p data-id="element-229">
                      Al acceder y utilizar este sitio web y adquirir nuestros
                      productos digitales, aceptas estar sujeto a estos términos
                      y condiciones. Si no estás de acuerdo con alguna parte de
                      estos términos, no debes utilizar nuestros servicios.
                    </p>
                    <h4 className="font-bold text-navy text-lg" data-id="element-230">
                      2. Productos Digitales
                    </h4>
                    <p data-id="element-231">
                      Los productos ofrecidos son guías digitales descargables
                      con contenido educativo. El acceso al producto se otorga
                      una vez confirmado el pago. El contenido es para uso
                      personal y no puede ser redistribuido, revendido ni
                      compartido sin autorización expresa.
                    </p>
                    <h4 className="font-bold text-navy text-lg" data-id="element-232">
                      3. Política de Reembolso
                    </h4>
                    <p data-id="element-233">
                      Debido a la naturaleza digital de nuestros productos,
                      todas las ventas son finales una vez que el contenido ha
                      sido entregado y descargado. Si experimentas problemas
                      técnicos con la descarga, contáctanos y te ayudaremos a
                      resolver el inconveniente.
                    </p>
                    <h4 className="font-bold text-navy text-lg" data-id="element-234">
                      4. Propiedad Intelectual
                    </h4>
                    <p data-id="element-235">
                      Todo el contenido, incluyendo textos, imágenes, plantillas
                      y materiales incluidos en nuestros productos digitales,
                      está protegido por derechos de autor. Queda prohibida su
                      reproducción, distribución o modificación sin
                      consentimiento previo por escrito.
                    </p>
                    <h4 className="font-bold text-navy text-lg" data-id="element-236">
                      5. Limitación de Responsabilidad
                    </h4>
                    <p data-id="element-237">
                      Los resultados obtenidos al aplicar el contenido de
                      nuestras guías pueden variar según el esfuerzo, dedicación
                      y circunstancias individuales de cada usuario. No
                      garantizamos ingresos específicos ni resultados
                      financieros determinados.
                    </p>
                    <h4 className="font-bold text-navy text-lg" data-id="element-238">
                      6. Modificaciones
                    </h4>
                    <p data-id="element-239">
                      Nos reservamos el derecho de modificar estos términos y
                      condiciones en cualquier momento. Las modificaciones
                      entrarán en vigor desde su publicación en esta página.
                    </p>
                  </>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}